<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-form :loading="isLoading" @submit-valid="onSave">
    <oxd-form-row>
      <oxd-grid :cols="4" class="orangehrm-full-width-grid">
        <template v-if="attendanceRecord.previousRecord">
          <oxd-grid-item
            :class="
              !attendanceRecord.previousRecord.note ? '--span-column-2' : ''
            "
          >
            <oxd-input-group :label="$t('attendance.punched_in_time')">
              <oxd-text type="subtitle-2">
                {{ previousAttendanceRecordDate }} -
                {{ previousAttendanceRecordTime }}
                <oxd-text
                  tag="span"
                  class="orangehrm-attendance-punchedIn-timezone"
                >
                  {{ `(GMT ${previousRecordTimezone})` }}
                </oxd-text>
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>

          <oxd-grid-item v-if="attendanceRecord.previousRecord.note">
            <oxd-input-group :label="$t('attendance.punched_in_note')">
              <oxd-text type="subtitle-2">
                {{ attendanceRecord.previousRecord.note }}
              </oxd-text>
            </oxd-input-group>
          </oxd-grid-item>
        </template>

        <!-- Date Selector -->
        <oxd-grid-item class="--offset-row-2">
          <date-input
            :key="attendanceRecord.time"
            v-model="attendanceRecord.date"
            :label="$t('general.date')"
            :rules="rules.date"
            :disabled="!isEditable"
            required
          />
        </oxd-grid-item>

        <!-- Time  Selector -->
        <oxd-grid-item class="--offset-row-2">
          <oxd-input-field
            v-model="attendanceRecord.time"
            :label="$t('general.time')"
            :disabled="!isEditable"
            :rules="rules.time"
            type="time"
            :placeholder="$t('attendance.hh_mm')"
            required
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>

    <!-- select timezone -->

    <oxd-grid v-if="isTimezoneEditable" :cols="2">
      <oxd-grid-item>
        <timezone-dropdown v-model="attendanceRecord.timezone" required />
      </oxd-grid-item>
    </oxd-grid>

    <!-- Note input -->
    <oxd-form-row v-if="false">
      <oxd-grid :cols="4" class="orangehrm-full-width-grid">
        <oxd-grid-item class="--span-column-2">
          <oxd-input-field
            v-model="attendanceRecord.note"
            :rules="rules.note"
            :label="$t('general.note')"
            :placeholder="$t('general.type_here')"
            type="textarea"
          />
        </oxd-grid-item>
      </oxd-grid>
    </oxd-form-row>
    <oxd-divider />
    <oxd-form-actions>
      <required-text />
      <submit-button
        :label="atype === 'in' ? '打卡進' : '簽退'"
        type="button"
        @click="captureLocationAndScanQR"
        class="action-button"
        :class="atype === 'in' ? 'action-button' : 'action-button out'"
      />
    </oxd-form-actions>
  </oxd-form>
  <camera-popup ref="cameraDialog"></camera-popup>
  <confirmation
    ref="ConfirmationDialog"
    title="系統訊息"
    :subtitle="confirmMessage"
    cancel-label="取消"
    confirm-label="是"
  ></confirmation>
</template>

<script>
import {
  required,
  validDateFormat,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {
  parseTime,
  parseDate,
  formatTime,
  formatDate,
  guessTimezone,
  setClockInterval,
  getStandardTimezone,
} from '@/core/util/helper/datefns';
import {promiseDebounce} from '@ohrm/oxd';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@ohrm/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {reloadPage, navigate} from '@/core/util/helper/navigation';
import TimezoneDropdown from '@/orangehrmAttendancePlugin/components/TimezoneDropdown.vue';
import {Html5Qrcode} from 'html5-qrcode';
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';
import CameraScanQRCodeDialog from '@ohrm/components/dialogs/CameraScanQRCodeDialog';
import CryptoJS from 'crypto-js';
import ConfirmationDialog from '@/core/components/dialogs/ConfirmationDialog';

const attendanceRecordModal = {
  date: null,
  time: null,
  note: null,
  timezone: null,
  previousRecord: null,
};

export default {
  name: 'RecordAttendance',
  components: {
    'timezone-dropdown': TimezoneDropdown,
    'camera-popup': CameraScanQRCodeDialog,
    confirmation: ConfirmationDialog,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    isTimezoneEditable: {
      type: Boolean,
      default: false,
    },
    attendanceRecordId: {
      type: Number,
      default: null,
    },
    employeeId: {
      type: Number,
      default: null,
    },
    date: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const apiPath = props.employeeId
      ? `/api/v2/attendance/employees/${props.employeeId}/records`
      : '/api/v2/attendance/records';
    const http = new APIService(window.appGlobal.baseUrl, apiPath);
    const {jsDateFormat, userDateFormat, timeFormat, jsTimeFormat} =
      useDateFormat();
    const {locale} = useLocale();

    const isLoading = ref(false);
    const isCameraOpen = ref(false);
    const qrReader = ref(null);
    const type = ref(props.type);
    console.log('attendance type', type.value);
    let html5QrCode = null;
    let QRCode = null;
    let latitude = null;
    let longitude = null;
    let confirmMessage = ref('');
    return {
      http,
      locale,
      timeFormat,
      jsTimeFormat,
      jsDateFormat,
      userDateFormat,
      isLoading,
      isCameraOpen,
      qrReader,
      QRCode,
      latitude,
      longitude,
      confirmMessage,
      atype: type.value,
    };
  },
  data() {
    return {
      attendanceRecord: {...attendanceRecordModal},
      rules: {
        date: [
          required,
          validDateFormat(this.userDateFormat),
          promiseDebounce(this.validateDate, 500),
        ],
        time: [required, promiseDebounce(this.validateDate, 500)],
        note: [shouldNotExceedCharLength(250)],
      },
      previousRecordTimezone: null,
    };
  },
  computed: {
    previousAttendanceRecordDate() {
      if (!this.attendanceRecord?.previousRecord) return null;
      return formatDate(
        parseDate(this.attendanceRecord.previousRecord.userDate),
        this.jsDateFormat,
        {locale: this.locale},
      );
    },
    previousAttendanceRecordTime() {
      if (!this.attendanceRecord?.previousRecord) return null;
      return formatTime(
        parseTime(
          this.attendanceRecord.previousRecord.userTime,
          this.timeFormat,
        ),
        this.jsTimeFormat,
      );
    },
  },
  beforeMount() {
    this.isLoading = true;
    // set default timezone
    if (this.isTimezoneEditable) {
      const tz = guessTimezone();
      this.attendanceRecord.timezone = {
        id: tz.name,
        label: tz.label,
        _name: tz.name,
        _offset: tz.offset,
      };
    }

    // fetch and set attendance record on initial load
    this.setCurrentDateTime()
      .then(() => {
        // then set record date/time every minute
        !this.date &&
          !this.isEditable &&
          setClockInterval(this.setCurrentDateTime, 60000);
        let url = '/api/v2/attendance/records/latest';
        if (this.employeeId) {
          url = `/api/v2/attendance/records/latest?empNumber=${this.employeeId}`;
        }
        return this.attendanceRecordId
          ? this.http.request({method: 'GET', url})
          : null;
      })

      .then((response) => {
        if (response) {
          const {data} = response.data;
          this.attendanceRecord.previousRecord = data.punchIn;
        }
      })
      .then(() => {
        this.previousRecordTimezone = getStandardTimezone(
          this.attendanceRecord.previousRecord?.offset,
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    confirm(message, data) {
      this.confirmMessage = message;
      this.$refs.ConfirmationDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.http
            .request({
              method: 'GET',
              url: '/../api/AttendanceApprove.php',
              params: {
                id: data.id,
                type: data.state.id == 'PUNCHED IN' ? 'in' : 'out',
                action: 'request',
              },
              // Prevent triggering response interceptor on 400
              validateStatus: (status) => {
                return (status >= 200 && status < 300) || status == 400;
              },
            })
            .then((res) => {
              reloadPage();
            });
        } else {
          reloadPage();
        }
      });
    },
    onSave() {
      this.isLoading = true;

      const timezone = guessTimezone();

      this.http
        .request({
          method: this.attendanceRecordId ? 'PUT' : 'POST',
          data: {
            date: this.attendanceRecord.date,
            time: this.attendanceRecord.time,
            note: this.attendanceRecord.note,
            timezoneOffset:
              this.attendanceRecord.timezone?._offset ?? timezone.offset,
            timezoneName: this.attendanceRecord.timezone?.id ?? timezone.name,
            qrcode: this.QRCode,
            latitude: this.latitude,
            longitude: this.longitude,
          },
        })
        .then((response) => {
          const data = response.data.data;
          console.log('response.data', data);
          let message = '';
          if (data.state.id == 'PUNCHED IN') {
            message = data.punchIn.note;
          } else if (data.state.id == 'PUNCHED OUT') {
            message = data.punchOut.note;
          }
          if (message) {
            this.confirm(message, data);
            return false;
          } else {
            reloadPage();
            return this.$toast.saveSuccess();
          }
        });
    },
    setCurrentDateTime() {
      return new Promise((resolve, reject) => {
        this.http
          .request({method: 'GET', url: '/api/v2/attendance/current-datetime'})
          .then((res) => {
            const {utcDate, utcTime} = res.data.data;
            const currentDate = parseDate(
              `${utcDate} ${utcTime} +00:00`,
              'yyyy-MM-dd HH:mm xxx',
            );
            this.attendanceRecord.date =
              this.date ?? formatDate(currentDate, 'yyyy-MM-dd');
            this.attendanceRecord.time = formatDate(currentDate, 'HH:mm');
            resolve();
          })
          .catch((error) => reject(error));
      });
    },
    validateDate() {
      if (!this.attendanceRecord.date || !this.attendanceRecord.time) {
        return true;
      }
      if (parseDate(this.attendanceRecord.date) === null) {
        return true;
      }
      const tzOffset = (new Date().getTimezoneOffset() / 60) * -1;
      return new Promise((resolve) => {
        this.http
          .request({
            method: 'GET',
            url: `/api/v2/attendance/${
              this.attendanceRecordId ? 'punch-out' : 'punch-in'
            }/overlaps`,
            params: {
              date: this.attendanceRecord.date,
              time: this.attendanceRecord.time,
              timezoneOffset:
                this.attendanceRecord.timezone?._offset ?? tzOffset,
              empNumber: this.employeeId,
            },
            // Prevent triggering response interceptor on 400
            validateStatus: (status) => {
              return (status >= 200 && status < 300) || status == 400;
            },
          })
          .then((res) => {
            const {data, error} = res.data;
            if (error) {
              return resolve(error.message);
            }
            return data.valid === true
              ? resolve(true)
              : resolve(this.$t('attendance.overlapping_records_found'));
          });
      });
    },
    async captureLocationAndScanQR() {
      try {
        // 步驟1: 獲取GPS位置
        const position = await this.getGPSLocation();
        const {latitude, longitude} = position.coords;

        // 步驟2: 打開相機掃描QR碼
        const qrCode = await this.startScanning(latitude, longitude);

        console.log('qrCode', qrCode);
      } catch (error) {
        console.error('錯誤:', error);
        // this.$toast.error({title: '無法獲取位置 或 掃描QR碼', message: null});

        const qrCode = await this.startScanning(0, 0);
        // const qrCode = await this.startScanning(22.3791673, 114.1975382);
      }
    },

    getGPSLocation() {
      return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
          // resolve({coords: {latitude: 0, longitude: 0}});
          reject(new Error('您的瀏覽器不支援GPS'));
        }
      });
    },

    handleScannedData(latitude, longitude, qrCode) {
      // 處理掃描到的數據，例如傳送到後端
      console.log('位置:', latitude, longitude);
      console.log('QR碼:', qrCode);
      // 可以在這裡調用API或者進行其他後續處理
      // alert(`正確 QRCODE! ${qrCode} ${latitude} ${longitude}`);
      this.QRCode = qrCode;
      this.latitude = latitude;
      this.longitude = longitude;
      this.onSave();
    },
    startScanning(latitude, longitude) {
      this.$refs.cameraDialog.showDialog().then(() => {
        this.stopScanning();
      });
      let qrcodes = [];
      this.$nextTick(() => {
        // 開始相機掃描
        this.html5QrCode = new Html5Qrcode('cameraQRCode');
        this.isCameraOpen = true;
        this.html5QrCode
          .start(
            {facingMode: 'environment'}, // 後置相機
            {fps: 10, qrbox: {width: 250, height: 250}},
            (decodedText) => {
              try {
                const qrcode = this.decrypt(decodedText);
                const jsonstr = JSON.parse(qrcode);
                const hash = CryptoJS.MD5(`!@#$0001${jsonstr[1]}`).toString();
                const trimhash = hash.substr(0, 5);
                if (latitude === 0 && longitude === 0) {
                  latitude = jsonstr[3];
                  longitude = jsonstr[4];
                }
                if (trimhash === jsonstr[2]) {
                  if (!qrcodes.includes(decodedText)) {
                    qrcodes.push(decodedText);
                    if (qrcodes.length >= 3) {
                      this.handleScannedData(
                        latitude,
                        longitude,
                        qrcodes.join('@'),
                      );
                      this.stopScanning();
                    }
                  }
                } else {
                  alert('無效 QRCODE!2');
                  this.stopScanning();
                }
              } catch (e) {
                alert('無效 QRCODE!1');
                this.stopScanning();
              }
            },
            (errorMessage) => {
              // alert('掃描錯誤:' + errorMessage);
              // this.stopScanning();
            },
          )
          .catch((error) => {
            alert('無法啟動相機:', error);
            // this.isCameraOpen = false;
            this.stopScanning();
          });
      });
    },
    stopScanning() {
      if (this.html5QrCode && this.isCameraOpen) {
        this.html5QrCode
          .stop()
          .then(() => {
            this.isCameraOpen = false;
            this.$refs.cameraDialog.onCancel();
            console.log('相機已停止');
          })
          .catch((error) => {
            console.error('停止相機時發生錯誤:', error);
          });
      }
    },
    decrypt(encryptedStr) {
      const hexIv = '00000000000000000000000000000000';
      const key = CryptoJS.SHA256('O.f)v7vs2yEDiD1v').toString(
        CryptoJS.enc.Hex,
      );
      const iv = CryptoJS.enc.Hex.parse(hexIv);
      const decrypted = CryptoJS.AES.decrypt(
        encryptedStr,
        CryptoJS.enc.Hex.parse(key),
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        },
      );
      return decrypted.toString(CryptoJS.enc.Utf8);
    },
  },
};
</script>

<style src="./record-attendance.scss" lang="scss" scoped></style>
<style type="text/css">
@media (max-width: 500px) {
  .action-button {
    width: 100%;
  }
}
.action-button.out {
  background-color: #ff8226;
}
</style>
